/*
 * Sign-in: Zitadel (OIDC) and nothing else.
 *
 * Backend side: @backstage/plugin-auth-backend-module-oidc-provider registers
 * provider id "oidc" and is configured under auth.providers.oidc.<environment>.
 * Frontend side (this module): an OAuth2 utility API for that provider and a
 * sign-in page that offers only it. No guest provider anywhere.
 */
import {
  ApiBlueprint,
  configApiRef,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';
import type {
  ApiRef,
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';

export const zitadelAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({ id: 'auth.zitadel' });

const zitadelAuthApi = ApiBlueprint.make({
  name: 'zitadel',
  params: define =>
    define({
      api: zitadelAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: { id: 'oidc', title: 'Zitadel', icon: () => null },
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: ['openid', 'profile', 'email'],
          popupOptions: { size: { width: 480, height: 720 } },
        }),
    }),
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'zitadel',
          title: 'Zitadel',
          message: 'Sign in with your Newton account',
          apiRef: zitadelAuthApiRef,
        }}
      />
    ),
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [zitadelAuthApi, signInPage],
});
